/**
 * How to describe the card on file, and when to warn about it.
 *
 * Pure and separately tested, because the interesting part is a date comparison whose
 * off-by-one is invisible on screen: a card expiring "06/2028" is valid through the
 * LAST day of June, not the first. Treating it as expired on 2028-06-01 would tell a
 * customer with a working card that their payment is about to fail.
 */

export type CardStatus = 'ok' | 'expiring' | 'expired';

export interface CardLike {
  brand: string | null;
  last4: string;
  exp_month: number | null;
  exp_year: number | null;
}

/** Brand tokens we display with proper casing. Anything else is shown as returned. */
const BRAND_LABEL: Record<string, string> = {
  visa: 'Visa',
  mastercard: 'Mastercard',
  amex: 'American Express',
  discover: 'Discover',
  diners: 'Diners Club',
  jcb: 'JCB',
  unionpay: 'UnionPay',
  eftpos_au: 'Eftpos',
  cartes_bancaires: 'Cartes Bancaires',
};

/**
 * `Visa` from `visa`. NEVER translated: a card brand is a proper noun, and a localised
 * "维萨" would not match the word printed on the card the admin is holding.
 *
 * An unknown token falls back to the token itself rather than to "Card", so a brand
 * Stripe adds next year still identifies the card instead of becoming anonymous.
 */
export function brandLabel(brand: string | null): string {
  if (!brand) return '';
  return BRAND_LABEL[brand.toLowerCase()] ?? brand;
}

/**
 * Whether the card is fine, close to expiring, or already expired.
 *
 * A card is valid through the END of its expiry month, which is the off-by-one this
 * function exists to get right.
 *
 * `expiring` covers the EXPIRY MONTH and the one before it — measured, not estimated: a
 * card ending 06/2028 warns from 1 May and stays silent through April. That is one full
 * monthly renewal cycle of notice, which is enough to act on, while a wider window makes
 * the line permanent and therefore ignorable.
 *
 * `now` is injected so the tests are not date-dependent and so the caller uses one
 * clock. Missing expiry data yields 'ok': we do not warn about something we cannot see.
 */
export function cardStatus(card: CardLike, now: Date): CardStatus {
  if (!card.exp_month || !card.exp_year) return 'ok';
  // The first instant AFTER the card's validity: midnight on the 1st of the following
  // month. Month is 0-indexed in Date, so `exp_month` already means "the month after"
  // when passed unadjusted.
  const expiresAfter = new Date(card.exp_year, card.exp_month, 1);
  if (now >= expiresAfter) return 'expired';
  const soon = new Date(now.getFullYear(), now.getMonth() + 2, 1);
  return expiresAfter <= soon ? 'expiring' : 'ok';
}

/**
 * `06 / 2028`, padded.
 *
 * Not `toLocaleDateString`: an expiry is not a date, it is the two fields printed on
 * the card, and a locale that renders "June 2028" or "2028年6月" stops matching what
 * the admin is looking at while they compare.
 */
export function expiryLabel(card: CardLike): string {
  if (!card.exp_month || !card.exp_year) return '';
  return `${String(card.exp_month).padStart(2, '0')} / ${card.exp_year}`;
}
